<template>
  <div v-if="isFlagEnabled" class="pay-range-version-drawer">
    <div class="pay-range-version-drawer__panel">
      <!-- Header -->
      <div class="pay-range-version-drawer__header">
        <div class="pay-range-version-drawer__header-top">
          <div class="pay-range-version-drawer__employee">
            <v-avatar v-if="employee.avatar" size="40" class="pay-range-version-drawer__avatar">
              <img :src="employee.avatar" :alt="employee.name" />
            </v-avatar>
            <div class="pay-range-version-drawer__employee-info">
              <h3 class="pay-range-version-drawer__employee-name">{{ employee.name }}</h3>
              <p v-if="employee.title" class="pay-range-version-drawer__employee-title">
                {{ employee.title }}
              </p>
            </div>
          </div>
          <div class="pay-range-version-drawer__header-actions">
            <div class="pay-range-version-drawer__badges">
              <v-chip small class="pay-range-version-drawer__version-badge">
                {{ payRangeVersion.code }}
              </v-chip>
              <v-chip small class="pay-range-version-drawer__in-force-badge">{{ statusBadgeText }}</v-chip>
              <v-chip small :class="stateBadgeClass">
                {{ stateBadgeText }}
              </v-chip>
            </div>
            <div class="pay-range-version-drawer__effective-dates">
              <span class="pay-range-version-drawer__date-text">
                <template v-if="payRangeVersion.effective_from">
                  Effective from {{ formatFairPayDate(payRangeVersion.effective_from) }}
                </template>
                <template v-if="payRangeVersion.effective_to">
                  · Effective to {{ formatFairPayDate(payRangeVersion.effective_to) }}
                </template>
              </span>
            </div>
            <div class="pay-range-version-drawer__icons">
              <ChatterIcon
                :unread="unreadChatter ? 1 : 0"
                class="pay-range-version-drawer__chatter-icon"
                @click="$emit('open-chatter')"
              />
              <v-icon
                class="pay-range-version-drawer__close-icon"
                aria-label="Close drawer"
                @click="$emit('close')"
              >
                mdi-close
              </v-icon>
            </div>
          </div>
        </div>

        <!-- PeriodBar -->
        <PeriodBar
          v-if="state !== 'loading'"
          :style="periodBarStyle"
          class="pay-range-version-drawer__period-bar"
          :show-label="false"
          :show-open-ended-marker="isOpenEnded"
        />
        <div v-else class="pay-range-version-drawer__period-bar-skeleton"></div>
      </div>

      <!-- Body -->
      <div class="pay-range-version-drawer__body">
        <template v-if="state !== 'loading'">
          <!-- Alert -->
          <v-alert
            :type="alertType"
            class="pay-range-version-drawer__alert"
            border="left"
            colored-border
          >
            {{ alertText }}
          </v-alert>

          <!-- Pay range version card -->
          <div class="pay-range-version-drawer__card">
            <p class="pay-range-version-drawer__hint">This is a hint text to help user.</p>

            <!-- Position section -->
            <div class="pay-range-version-drawer__section">
              <h4 class="pay-range-version-drawer__section-title">
                Pay range comes from position
              </h4>
              <div class="pay-range-version-drawer__position-info">
                <p class="pay-range-version-drawer__position-code">
                  {{ sourcePosition.code }} - {{ sourcePosition.name }}
                </p>
                <p
                  v-if="sourcePosition.primaryAssignmentPeriod"
                  class="pay-range-version-drawer__position-period"
                >
                  Primary assignment: {{ sourcePosition.primaryAssignmentPeriod }}
                </p>
                <p
                  v-if="sourcePosition.subscriptionPeriod"
                  class="pay-range-version-drawer__subscription-period"
                >
                  {{ subscriptionLineText }}
                </p>
              </div>
            </div>

            <!-- Pay range details -->
            <div class="pay-range-version-drawer__section">
              <h4 class="pay-range-version-drawer__section-title">Pay range at 1 FTE</h4>
              <div class="pay-range-version-drawer__pay-range-details">
                <p class="pay-range-version-drawer__pay-range-code">
                  {{ payRangeVersion.code }}
                </p>
                <p
                  v-if="payRangeVersion.jobCategory || payRangeVersion.level"
                  class="pay-range-version-drawer__job-info"
                >
                  {{ payRangeVersion.jobCategory }} › {{ payRangeVersion.level }}
                </p>
                <p v-if="payRangeVersion.location" class="pay-range-version-drawer__location">
                  {{ payRangeVersion.location }}
                </p>
                <p
                  v-if="payRangeVersion.workArrangement"
                  class="pay-range-version-drawer__work-arrangement"
                >
                  {{ payRangeVersion.workArrangement }}
                </p>
                <p
                  v-if="payRangeVersion.currency && payRangeVersion.payBasis"
                  class="pay-range-version-drawer__currency-basis"
                >
                  {{ payRangeVersion.currency }} · {{ payRangeVersion.payBasis }}
                </p>

                <!-- Amounts at different readiness levels -->
                <div
                  v-if="payRangeVersion.amounts"
                  class="pay-range-version-drawer__amounts"
                >
                  <div class="pay-range-version-drawer__amount-item">
                    <span class="pay-range-version-drawer__amount-label">0%:</span>
                    <span class="pay-range-version-drawer__amount-value">
                      {{ formatAmount(payRangeVersion.amounts['0']) }}
                    </span>
                  </div>
                  <div class="pay-range-version-drawer__amount-item">
                    <span class="pay-range-version-drawer__amount-label">50%:</span>
                    <span class="pay-range-version-drawer__amount-value">
                      {{ formatAmount(payRangeVersion.amounts['50']) }}
                    </span>
                  </div>
                  <div class="pay-range-version-drawer__amount-item">
                    <span class="pay-range-version-drawer__amount-label">100%:</span>
                    <span class="pay-range-version-drawer__amount-value">
                      {{ formatAmount(payRangeVersion.amounts['100']) }}
                    </span>
                  </div>
                  <div class="pay-range-version-drawer__amount-item">
                    <span class="pay-range-version-drawer__amount-label">150%:</span>
                    <span class="pay-range-version-drawer__amount-value">
                      {{ formatAmount(payRangeVersion.amounts['150']) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Loading skeleton -->
        <template v-else>
          <div class="pay-range-version-drawer__skeleton">
            <div class="pay-range-version-drawer__skeleton-alert"></div>
            <div class="pay-range-version-drawer__skeleton-content"></div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';
import PeriodBar from '@components/PeriodBar/PeriodBar.vue';
import ChatterIcon from '@components/ChatterIcon/ChatterIcon.vue';
import { LD_FLAG_IDEA_MDP_429 } from '@components/constants/launchDarklyFlagKeys';
import { formatFairPayDate } from '@components/utils/fairPayFormat';
import { versionStatusBadge } from '@components/FairPay/payRangeBadges';

const props = defineProps({
  state: {
    type: String,
    required: true,
    validator: (value) => ['current', 'future', 'lapsed', 'loading'].includes(value),
  },
  employee: {
    type: Object,
    required: true,
    validator: (value) => Boolean(value.name),
  },
  payRangeVersion: {
    type: Object,
    required: true,
    validator: (value) => Boolean(value.code),
  },
  sourcePosition: {
    type: Object,
    required: true,
    validator: (value) => Boolean(value.code),
  },
  unreadChatter: {
    type: Boolean,
    default: false,
  },
  /** Host-resolved flag. When null the drawer falls back to the injected LaunchDarkly client. */
  featureEnabled: {
    type: Boolean,
    default: null,
  },
});

defineEmits(['open-chatter', 'close']);

// Feature flag check
const ldClient = inject('ldClient', null);
const ldUser = inject('ldUser', null);

const isFlagEnabled = computed(() => {
  if (props.featureEnabled !== null) return props.featureEnabled;
  if (!ldClient || !ldUser) return false;
  return ldClient.variation(LD_FLAG_IDEA_MDP_429, ldUser, false);
});

// State badge configuration
const stateBadgeClass = computed(() => {
  const baseClass = 'pay-range-version-drawer__state-badge';
  return {
    [baseClass]: true,
    [`${baseClass}--current`]: props.state === 'current',
    [`${baseClass}--future`]: props.state === 'future',
    [`${baseClass}--lapsed`]: props.state === 'lapsed',
  };
});

const statusBadgeText = computed(
  () => (versionStatusBadge(props.payRangeVersion) || versionStatusBadge('in-force')).label
);

const stateBadgeText = computed(() => {
  const stateMap = {
    current: 'Current',
    future: 'Future',
    lapsed: 'Lapsed',
    loading: 'Current',
  };
  return stateMap[props.state] || 'Current';
});

// Alert configuration
const alertType = computed(() => {
  const typeMap = {
    current: 'success',
    future: 'info',
    lapsed: 'warning',
    loading: 'info',
  };
  return typeMap[props.state] || 'info';
});

const alertText = computed(() => {
  const code = props.payRangeVersion.code || '';
  const replacesVersionCode = props.payRangeVersion.replacesVersionCode || '';
  const effectiveTo = props.payRangeVersion.effective_to;

  switch (props.state) {
    case 'current':
      return `This is the current version of pay range ${code}`;
    case 'future':
      if (props.payRangeVersion.effective_from && replacesVersionCode) {
        return `This version takes effect on ${formatFairPayDate(
          props.payRangeVersion.effective_from
        )} and replaces ${replacesVersionCode}`;
      }
      return `This version takes effect on ${formatFairPayDate(props.payRangeVersion.effective_from)}`;
    case 'lapsed':
      if (effectiveTo) {
        return `This version lapsed on ${formatFairPayDate(
          effectiveTo
        )}. The employee's subscription ended with it`;
      }
      return 'This version has lapsed';
    default:
      return '';
  }
});

// Subscription line text — names the pay range (P-1449), not the version (P-1449.V2)
const subscriptionLineText = computed(() => {
  const code = props.payRangeVersion.rangeCode || props.payRangeVersion.code || '';
  const period = props.sourcePosition.subscriptionPeriod || '';

  switch (props.state) {
    case 'current':
      return `Subscribed to ${code} from ${period} · open`;
    case 'future':
      if (props.payRangeVersion.effective_from) {
        return `${period} · open — moves to ${props.payRangeVersion.code} on ${formatFairPayDate(
          props.payRangeVersion.effective_from
        )}`;
      }
      return `${period} · open`;
    case 'lapsed':
      return `Subscribed to ${code} ${period}`;
    default:
      return period;
  }
});

// PeriodBar styling
const periodBarStyle = computed(() => {
  const styles = {
    current: {
      backgroundColor: '#dcfae6',
      border: '1.5px solid #75e0a7',
      borderRadius: '4px',
      padding: '4px 8px',
    },
    future: {
      backgroundColor: '#eff8ff',
      border: '1.5px solid #b2ddff',
      borderRadius: '4px',
      padding: '4px 8px',
    },
    lapsed: {
      backgroundColor: '#f2f4f7',
      border: '1.5px solid #d0d5dd',
      borderRadius: '4px',
      padding: '4px 8px',
    },
  };
  return styles[props.state] || styles.current;
});

const isOpenEnded = computed(() => {
  return props.state === 'current' || !props.payRangeVersion.effective_to;
});

// Utility functions
const formatAmount = (amount) => {
  if (!amount) return '—';
  return amount;
};
</script>

<style scoped>
.pay-range-version-drawer__panel {
  background: #ffffff;
  border: 1px solid #eaecf0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.pay-range-version-drawer__header {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 0 24px;
}

.pay-range-version-drawer__header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 0 0;
}

.pay-range-version-drawer__employee {
  display: flex;
  gap: 12px;
  align-items: center;
}

.pay-range-version-drawer__employee-info {
  display: flex;
  flex-direction: column;
}

.pay-range-version-drawer__employee-name {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #344054;
  margin: 0;
}

.pay-range-version-drawer__employee-title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

.pay-range-version-drawer__header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.pay-range-version-drawer__badges {
  display: flex;
  gap: 8px;
  align-items: center;
}

.pay-range-version-drawer__version-badge {
  background-color: #f9fafb !important;
  color: #344054 !important;
}

.pay-range-version-drawer__in-force-badge {
  background-color: #7f56d9 !important;
  color: #ffffff !important;
}

.pay-range-version-drawer__state-badge--current {
  background-color: #ecfdf5 !important;
  color: #067647 !important;
}

.pay-range-version-drawer__state-badge--future {
  background-color: #eff8ff !important;
  color: #175cd3 !important;
}

.pay-range-version-drawer__state-badge--lapsed {
  background-color: #f2f4f7 !important;
  color: #344054 !important;
}

.pay-range-version-drawer__effective-dates {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.pay-range-version-drawer__icons {
  display: flex;
  gap: 16px;
  align-items: center;
}

.pay-range-version-drawer__chatter-icon {
  cursor: pointer;
}

.pay-range-version-drawer__close-icon {
  cursor: pointer;
  color: #475467;
}

.pay-range-version-drawer__period-bar {
  margin-top: 8px;
}

.pay-range-version-drawer__period-bar-skeleton {
  height: 24px;
  background-color: #eaecf0;
  border-radius: 6px;
  margin-top: 8px;
}

.pay-range-version-drawer__body {
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 0 24px 24px;
}

.pay-range-version-drawer__alert {
  margin: 0;
}

.pay-range-version-drawer__card {
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pay-range-version-drawer__hint {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

.pay-range-version-drawer__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.pay-range-version-drawer__section-title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  margin: 0;
}

.pay-range-version-drawer__position-info,
.pay-range-version-drawer__pay-range-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pay-range-version-drawer__position-code,
.pay-range-version-drawer__pay-range-code {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  margin: 0;
}

.pay-range-version-drawer__position-period,
.pay-range-version-drawer__subscription-period,
.pay-range-version-drawer__job-info,
.pay-range-version-drawer__location,
.pay-range-version-drawer__work-arrangement,
.pay-range-version-drawer__currency-basis {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
  margin: 0;
}

.pay-range-version-drawer__amounts {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}

.pay-range-version-drawer__amount-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.pay-range-version-drawer__amount-label {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  min-width: 40px;
}

.pay-range-version-drawer__amount-value {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.pay-range-version-drawer__skeleton {
  display: flex;
  flex-direction: column;
  gap: 17px;
}

.pay-range-version-drawer__skeleton-alert {
  height: 48px;
  background-color: #f2f4f7;
  border-radius: 8px;
}

.pay-range-version-drawer__skeleton-content {
  height: 200px;
  background-color: #f2f4f7;
  border-radius: 12px;
}
</style>
